import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { MessageSquarePlus, Trash2, MessageSquare } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

const HIGHLIGHT_COLORS = [
  'rgba(253, 230, 138, 0.45)',
  'rgba(167, 243, 208, 0.45)',
  'rgba(191, 219, 254, 0.45)',
  'rgba(253, 164, 175, 0.45)',
  'rgba(216, 180, 254, 0.45)',
];

function normalizeSpaces(html) {
  if (!html) return html;
  return html
    .replace(/&nbsp;/g, ' ')
    .replace(/<p>\s*<\/p>/g, '<p><br></p>');
}

function getTextNodes(node) {
  const nodes = [];
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null);
  let n;
  while ((n = walker.nextNode())) nodes.push(n);
  return nodes;
}

function getGlobalOffset(container, targetNode, localOffset) {
  const textNodes = getTextNodes(container);
  let globalOffset = 0;
  for (const tn of textNodes) {
    if (tn === targetNode) return globalOffset + localOffset;
    globalOffset += tn.textContent.length;
  }
  return globalOffset;
}

function findRangeForOffsets(container, startOffset, endOffset) {
  const textNodes = getTextNodes(container);
  let currentOffset = 0;
  let startNode = null, startLocal = 0;
  let endNode = null, endLocal = 0;

  for (const tn of textNodes) {
    const len = tn.textContent.length;
    if (!startNode && currentOffset + len > startOffset) {
      startNode = tn;
      startLocal = startOffset - currentOffset;
    }
    if (!endNode && currentOffset + len >= endOffset) {
      endNode = tn;
      endLocal = endOffset - currentOffset;
      break;
    }
    currentOffset += len;
  }

  if (!startNode || !endNode) return null;
  try {
    const range = document.createRange();
    range.setStart(startNode, startLocal);
    range.setEnd(endNode, endLocal);
    return range;
  } catch {
    return null;
  }
}

function computeOverlayRects(contentEl, annotations) {
  if (!contentEl || !annotations.length) return [];
  const contentRect = contentEl.getBoundingClientRect();
  const rects = [];

  annotations.forEach((ann, idx) => {
    if (typeof ann.startOffset !== 'number' || typeof ann.endOffset !== 'number') return;
    const range = findRangeForOffsets(contentEl, ann.startOffset, ann.endOffset);
    if (!range) return;

    const clientRects = range.getClientRects();
    const color = HIGHLIGHT_COLORS[idx % HIGHLIGHT_COLORS.length];

    for (const r of clientRects) {
      if (r.width === 0 || r.height === 0) continue;
      rects.push({
        key: `${ann.id}_${Math.round(r.left)}_${Math.round(r.top)}`,
        left: r.left - contentRect.left,
        top: r.top - contentRect.top,
        width: r.width,
        height: r.height,
        color,
        tooltip: ann.comentario || '',
        annId: ann.id,
      });
    }
  });

  return rects;
}

export const AnnotatedContent = ({ html, annotations = [], readOnly = false, onAdd, onRemove }) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [popover, setPopover] = useState({ visible: false, x: 0, y: 0, text: '' });
  const [comment, setComment] = useState('');
  const pendingAnnotation = useRef(null);
  const [overlayRects, setOverlayRects] = useState([]);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, text: '' });

  const recalcOverlays = useCallback(() => {
    const contentEl = contentRef.current;
    if (!contentEl) { setOverlayRects([]); return; }
    requestAnimationFrame(() => {
      setOverlayRects(computeOverlayRects(contentEl, annotations));
    });
  }, [annotations]);

  useEffect(() => {
    recalcOverlays();
    const onResize = () => recalcOverlays();
    window.addEventListener('resize', onResize);
    const contentEl = contentRef.current;
    let observer;
    if (contentEl && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(recalcOverlays);
      observer.observe(contentEl);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      observer?.disconnect();
    };
  }, [recalcOverlays]);

  useEffect(() => {
    const timer = setTimeout(recalcOverlays, 60);
    return () => clearTimeout(timer);
  }, [html, recalcOverlays]);

  const handleContentMouseMove = useCallback((e) => {
    if (!contentRef.current || !overlayRects.length) {
      if (tooltip.visible) setTooltip({ visible: false, x: 0, y: 0, text: '' });
      return;
    }
    const contentRect = contentRef.current.getBoundingClientRect();
    const mx = e.clientX - contentRect.left;
    const my = e.clientY - contentRect.top;
    for (const r of overlayRects) {
      if (mx >= r.left && mx <= r.left + r.width && my >= r.top && my <= r.top + r.height && r.tooltip) {
        const containerRect = containerRef.current?.getBoundingClientRect() || contentRect;
        const tooltipX = e.clientX - containerRect.left;
        const tooltipY = e.clientY - containerRect.top;
        setTooltip({ visible: true, x: tooltipX, y: tooltipY - 8, text: r.tooltip });
        return;
      }
    }
    if (tooltip.visible) setTooltip({ visible: false, x: 0, y: 0, text: '' });
  }, [overlayRects, tooltip.visible]);

  const handleMouseUp = useCallback(() => {
    if (readOnly) return;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !selection.toString().trim()) return;

    const text = selection.toString().trim();
    if (text.length < 3) return;

    const contentEl = contentRef.current;
    if (!contentEl || !contentEl.contains(selection.anchorNode)) return;

    const range = selection.getRangeAt(0);
    const startOffset = getGlobalOffset(contentEl, range.startContainer, range.startOffset);
    const endOffset = getGlobalOffset(contentEl, range.endContainer, range.endOffset);

    const rect = range.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect() || { left: 0, top: 0 };

    pendingAnnotation.current = { startOffset, endOffset };

    setPopover({
      visible: true,
      x: rect.left - containerRect.left + rect.width / 2,
      y: rect.top - containerRect.top - 8,
      text,
    });
    setComment('');
  }, [readOnly]);

  const handleAdd = () => {
    if (!comment.trim() || !popover.text || !pendingAnnotation.current) return;
    onAdd?.({
      id: `ann_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      texto: popover.text,
      comentario: comment.trim(),
      startOffset: pendingAnnotation.current.startOffset,
      endOffset: pendingAnnotation.current.endOffset,
    });
    pendingAnnotation.current = null;
    setPopover({ visible: false, x: 0, y: 0, text: '' });
    setComment('');
    window.getSelection()?.removeAllRanges();
  };

  const normalizedHtml = useMemo(() => {
    // Sanitización XSS antes de inyectar HTML del editor enriquecido.
    // Mantenemos las etiquetas estándar de Quill (encabezados, listas, blockquote,
    // formato, alineación, color, links e imágenes) y bloqueamos vectores activos.
    const clean = DOMPurify.sanitize(html || '', {
      ALLOWED_TAGS: [
        'p', 'br', 'span', 'div', 'strong', 'em', 'u', 's', 'sub', 'sup',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
        'a', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr',
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'class', 'style'],
      ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|data:image\/(?:png|jpe?g|gif|webp|svg\+xml);base64,):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'link', 'meta'],
      FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit', 'srcdoc', 'formaction'],
      ALLOW_DATA_ATTR: false,
    });
    return normalizeSpaces(clean);
  }, [html]);

  return (
    <div className="relative" ref={containerRef}>
      {!readOnly && (
        <div className="text-xs text-blue-500 bg-blue-50 border border-blue-200 rounded px-3 py-1.5 mb-3 flex items-center gap-1.5" data-testid="annotation-hint">
          <MessageSquarePlus className="w-3.5 h-3.5" />
          Seleccione texto del contenido para agregar una anotación
        </div>
      )}

      <div style={{ position: 'relative' }}>
        {/* Overlay layer — behind text, non-destructive CSS highlights */}
        <div
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 1 }}
          data-testid="annotation-overlays"
        >
          {overlayRects.map((r) => (
            <div
              key={r.key}
              style={{
                position: 'absolute',
                left: `${r.left}px`,
                top: `${r.top}px`,
                width: `${r.width}px`,
                height: `${r.height}px`,
                background: r.color,
                borderRadius: '2px',
                pointerEvents: 'auto',
                cursor: 'help',
              }}
              title={r.tooltip}
              data-ann={r.annId}
            />
          ))}
        </div>

        {/* Content — sits on top so text is selectable, background transparent.
            Wrapped in `.ql-snow .ql-container` to match ReactQuill's DOM structure,
            so that quill.snow.css rules (headers, lists, blockquotes, fonts) apply
            consistently across all roles (USUARIO, LEGAL, SUPERVISOR, etc). */}
        <div className="ql-snow" style={{ position: 'relative', zIndex: 2 }}>
          <div className="ql-container" style={{ border: 0 }}>
            <div
              ref={contentRef}
              className="ql-editor"
              style={{ padding: 0 }}
              dangerouslySetInnerHTML={{ __html: normalizedHtml }}
              onMouseUp={handleMouseUp}
              onMouseMove={handleContentMouseMove}
              onMouseLeave={() => tooltip.visible && setTooltip({ visible: false, x: 0, y: 0, text: '' })}
              data-testid="annotated-content"
            />
          </div>
        </div>
      </div>

      {/* Tooltip custom para anotaciones */}
      {tooltip.visible && (
        <div
          className="absolute z-50 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg shadow-lg max-w-xs pointer-events-none"
          style={{ left: Math.max(0, tooltip.x - 80), top: tooltip.y, transform: 'translateY(-100%)' }}
          data-testid="annotation-tooltip"
        >
          <div className="flex items-center gap-1.5 mb-0.5">
            <MessageSquare className="w-3 h-3 text-amber-300 flex-shrink-0" />
            <span className="font-semibold text-amber-300">Anotación</span>
          </div>
          {tooltip.text}
        </div>
      )}

      {/* Popover */}
      {popover.visible && (
        <div
          className="absolute z-50 w-72 bg-white border border-slate-200 rounded-lg shadow-lg p-3 space-y-2"
          style={{ left: Math.max(0, popover.x - 144), top: popover.y - 140 }}
          data-testid="annotation-popover"
        >
          <p className="text-xs text-slate-500 truncate">
            Texto: "<span className="font-medium text-slate-700">{popover.text.slice(0, 60)}{popover.text.length > 60 ? '...' : ''}</span>"
          </p>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Escriba su comentario..."
            rows={2}
            className="text-sm"
            autoFocus
            data-testid="annotation-comment-input"
          />
          <div className="flex justify-end gap-1.5">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setPopover({ visible: false, x: 0, y: 0, text: '' }); window.getSelection()?.removeAllRanges(); }}
              className="h-7 text-xs"
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleAdd}
              disabled={!comment.trim()}
              className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="annotation-add-btn"
            >
              <MessageSquarePlus className="w-3 h-3 mr-1" />
              Agregar
            </Button>
          </div>
        </div>
      )}

      {/* Lista de anotaciones */}
      {annotations.length > 0 && (
        <div className="mt-4 border-t pt-3 space-y-2" data-testid="annotations-list">
          <p className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" />
            {annotations.length} anotación(es)
          </p>
          {annotations.map((ann, idx) => (
            <div
              key={ann.id}
              className="flex gap-2 p-2 rounded text-xs border"
              style={{ borderLeftWidth: 3, borderLeftColor: HIGHLIGHT_COLORS[idx % HIGHLIGHT_COLORS.length].replace('0.45', '1') }}
              data-testid={`annotation-item-${idx}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-slate-500 truncate italic">"{ann.texto}"</p>
                <p className="text-slate-700 mt-0.5">{ann.comentario}</p>
              </div>
              {!readOnly && (
                <button
                  onClick={() => onRemove?.(ann.id)}
                  className="text-red-400 hover:text-red-600 flex-shrink-0 self-start"
                  title="Eliminar anotación"
                  data-testid={`annotation-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnotatedContent;
